import { DateTime, type DurationLike } from "luxon";

/**
 * Quick-range inference for the floating FilterBar.
 *
 * Historically the bar carried two near-identical matchers — one anchored at
 * `now` (the legacy `lastDay/Week/Month/Year` presets) and one anchored at the
 * data `start` (caller-supplied synthetic ranges, e.g. simulation results). They
 * are the same algorithm with a different anchor, so they live here as a single
 * `inferRangeLabel`, driven by an `anchor` flag on each {@link QuickRange}.
 */

/** Where a quick range pins its window — to the wall clock or to the data start. */
export type RangeAnchor = "now" | "start";

export type QuickRange = {
  label: string;
  value: string;
  /** Span of the range in days — used for availability, inference and the start-anchored window. */
  days: number;
  /**
   * Window anchor. `"start"` (default) pins the window at the data start (synthetic
   * ranges, e.g. simulation results, where "last week" relative to *now* is
   * meaningless). `"now"` pins it at the wall clock (the legacy presets).
   */
  anchor?: RangeAnchor;
  /** Tolerance (days) when matching a window's span back to this range. Default {@link DEFAULT_SPAN_TOLERANCE_DAYS}. */
  spanTolerance?: number;
  /** `"now"`-anchored only: tolerance (days) for "is the window end near now". Default {@link DEFAULT_END_TOLERANCE_DAYS}. */
  endTolerance?: number;
  /**
   * `"now"`-anchored only: the calendar window subtracted from `now` to build the
   * range (and to test data availability). Carried as a Luxon duration so month/year
   * presets stay calendar-aware (`{ months: 1 }` ≠ 30 days). Defaults to `{ days }`.
   */
  nowWindow?: DurationLike;
};

/** Sentinel label meaning "no preset matches this window". */
export const QUICK_RANGE_CUSTOM = "custom";

/** Default span-match tolerance, in days. Also tolerates last-interval shortfalls in availability checks. */
export const DEFAULT_SPAN_TOLERANCE_DAYS = 0.5;

/** Default "end near now" tolerance, in days, for `"now"`-anchored ranges. */
export const DEFAULT_END_TOLERANCE_DAYS = 0.5;

/** A window start within this many ms of the data start counts as "anchored at the start". */
export const START_ANCHOR_TOLERANCE_MS = 60_000;

/**
 * The legacy now-anchored presets, as configuration. Used as the FilterBar's
 * default quick ranges when a caller supplies no `quickRanges`. Tolerances and
 * `nowWindow` reproduce the old `inferQuickRangeFromWindow` / `buildQuickDateOptions`
 * / `handleTimeRangeChange` behaviour exactly (calendar-aware month/year).
 */
export const DEFAULT_QUICK_RANGES: QuickRange[] = [
  {
    value: "lastDay",
    label: "text_last_day",
    days: 1,
    anchor: "now",
    spanTolerance: 0.2,
    endTolerance: 0.5,
    nowWindow: { days: 1 },
  },
  {
    value: "lastWeek",
    label: "text_last_week",
    days: 7,
    anchor: "now",
    spanTolerance: 0.5,
    endTolerance: 1,
    nowWindow: { weeks: 1 },
  },
  {
    value: "lastMonth",
    label: "text_last_month",
    days: 30,
    anchor: "now",
    spanTolerance: 2,
    endTolerance: 2,
    nowWindow: { months: 1 },
  },
  {
    value: "lastYear",
    label: "text_last_year",
    days: 365,
    anchor: "now",
    spanTolerance: 5,
    endTolerance: 5,
    nowWindow: { years: 1 },
  },
];

/** The calendar window a `"now"`-anchored range subtracts from `now` (falls back to its `days`). */
export const nowWindowOf = (range: QuickRange): DurationLike =>
  range.nowWindow ?? { days: range.days };

/** Does a `[start, end]` window (with precomputed `spanDays`) match a single quick range? */
function rangeMatches(
  range: QuickRange,
  start: DateTime,
  end: DateTime,
  spanDays: number,
  now: DateTime,
  dataStart: DateTime | null | undefined
): boolean {
  const spanTolerance = range.spanTolerance ?? DEFAULT_SPAN_TOLERANCE_DAYS;
  if (Math.abs(spanDays - range.days) > spanTolerance) return false;

  if ((range.anchor ?? "start") === "now") {
    const endTolerance = range.endTolerance ?? DEFAULT_END_TOLERANCE_DAYS;
    return Math.abs(end.diff(now, "days").as("days")) <= endTolerance;
  }

  // Start-anchored: the window must begin at the data start.
  if (!dataStart?.isValid) return false;
  return Math.abs(start.toMillis() - dataStart.toMillis()) <= START_ANCHOR_TOLERANCE_MS;
}

/**
 * Infer which quick range a `[start, end]` window matches, or {@link QUICK_RANGE_CUSTOM}.
 *
 * Matching is approximate: the span must be within `spanTolerance` of the range's
 * `days`, and the anchor must hold —
 * - `"now"`: the window end is within `endTolerance` days of `now`.
 * - `"start"`: the window start is within {@link START_ANCHOR_TOLERANCE_MS} of `dataStart`.
 *
 * Returns the first matching range's `value` (configuration order is priority).
 *
 * @param options.now      Wall clock, for `"now"`-anchored ranges. Defaults to `DateTime.now()`.
 * @param options.dataStart Data-range start, required by `"start"`-anchored ranges.
 */
export function inferRangeLabel(
  start: DateTime,
  end: DateTime,
  ranges: QuickRange[],
  options: { now?: DateTime; dataStart?: DateTime | null } = {}
): string {
  if (!start?.isValid || !end?.isValid) return QUICK_RANGE_CUSTOM;

  const now = options.now ?? DateTime.now();
  const spanDays = end.diff(start, "days").as("days");

  const match = ranges.find((range) =>
    rangeMatches(range, start, end, spanDays, now, options.dataStart)
  );

  return match?.value ?? QUICK_RANGE_CUSTOM;
}
